import React, { useState } from "react";
import Sidebar from "../../components/layout/Sidebar";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  ArrowLeft, 
  ShoppingCart, 
  FileText, 
  Users, 
  TrendingUp, 
  Shield, 
  Clock, 
  DollarSign, 
  BarChart3, 
  CheckCircle, 
  AlertCircle, 
  Search, 
  Settings, 
  Database, 
  Globe, 
  Zap,
  Target,
  Award,
  Briefcase,
  Calculator,
  Building,
  Phone,
  Mail,
  MapPin,
  CheckSquare,
  Package,
  CreditCard,
  FileCheck,
  UserCheck,
  Layers,
  Headphones 
} from "lucide-react";

const EProcurement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const title = "Advanced E-Procurement & Digital Procurement Solutions";
  const description = "Revolutionize your procurement operations with our comprehensive digital procurement platform. Streamline supplier management, automate tender processes, and achieve complete procurement transparency with cutting-edge technology designed for modern businesses.";

  const coreModules = {
    supplierManagement: {
      title: "Supplier Management System",
      icon: <Users className="w-8 h-8" />,
      description: "Complete supplier lifecycle management from registration to performance tracking",
      features: [
        "Supplier Registration & Onboarding Portal",
        "Vendor Database Management",
        "Supplier Performance Monitoring",
        "Qualification & Prequalification Systems",
        "Supplier Risk Assessment Tools",
        "Contract & Agreement Management",
        "Supplier Communication Platform",
        "Vendor Rating & Review System",
        "Multi-tier Supplier Networks",
        "Supplier Financial Health Monitoring",
        "Compliance & Certification Tracking",
        "Supplier Diversity Programs"
      ]
    },
    tenderManagement: {
      title: "Digital Tender Management",
      icon: <FileText className="w-8 h-8" />,
      description: "End-to-end tender management from publication to award",
      features: [
        "Online Tender Publication Portal",
        "RFP/RFQ Creation & Management",
        "Bid Submission & Evaluation System",
        "Multi-stage Tender Processes",
        "Technical & Financial Evaluation",
        "Bid Comparison & Analysis Tools",
        "Tender Document Management",
        "Automated Tender Notifications",
        "Bid Opening & Award Management",
        "Tender History & Audit Trails",
        "Contract Award & Management",
        "Appeal & Dispute Management"
      ]
    },
    purchaseOrderSystem: {
      title: "Purchase Order Automation",
      icon: <ShoppingCart className="w-8 h-8" />,
      description: "Automated purchase order processing and workflow management",
      features: [
        "Automated PO Generation",
        "Multi-level Approval Workflows",
        "Purchase Requisition Management",
        "Budget Control & Verification",
        "Goods Receipt Note (GRN) Processing",
        "Three-way Matching System",
        "Purchase Order Tracking",
        "Delivery Schedule Management",
        "Order Amendment & Cancellation",
        "Blanket Purchase Orders",
        "Emergency Purchase Procedures",
        "Mobile Purchase Approvals"
      ]
    },
    invoiceManagement: {
      title: "Invoice & Payment Processing",
      icon: <CreditCard className="w-8 h-8" />,
      description: "Automated invoice processing with integrated payment systems",
      features: [
        "Digital Invoice Receipt & Processing",
        "Automated Invoice Matching",
        "Payment Terms Management",
        "Electronic Payment Integration",
        "Invoice Approval Workflows",
        "Duplicate Invoice Detection",
        "Early Payment Discount Management",
        "Vendor Payment Scheduling",
        "Payment Status Tracking",
        "Tax Compliance & Reporting",
        "Invoice Dispute Resolution",
        "Payment Analytics & Reporting"
      ]
    },
    complianceTracking: {
      title: "Compliance & Risk Management",
      icon: <Shield className="w-8 h-8" />,
      description: "Comprehensive compliance monitoring and risk assessment tools",
      features: [
        "Regulatory Compliance Monitoring",
        "Policy Enforcement Automation",
        "Risk Assessment & Mitigation",
        "Audit Trail Management",
        "Compliance Reporting Dashboard",
        "Contract Compliance Tracking",
        "Procurement Policy Management",
        "Ethical Procurement Guidelines",
        "Anti-corruption Measures",
        "Environmental & Social Compliance",
        "Quality Assurance Standards",
        "Legal Compliance Verification"
      ]
    },
    analyticsReporting: {
      title: "Analytics & Business Intelligence",
      icon: <BarChart3 className="w-8 h-8" />,
      description: "Advanced analytics and reporting for data-driven procurement decisions",
      features: [
        "Real-time Procurement Dashboards",
        "Spend Analysis & Categorization",
        "Supplier Performance Analytics",
        "Cost Savings Tracking",
        "Procurement KPI Monitoring",
        "Budget vs Actual Analysis",
        "Trend Analysis & Forecasting",
        "Custom Report Generation",
        "Executive Summary Reports",
        "Market Intelligence Integration",
        "Predictive Analytics Tools",
        "Data Visualization & Charts"
      ]
    }
  };

  const additionalFeatures = [
    {
      category: "Digital Transformation",
      items: [
        "Paperless Procurement Processes",
        "Digital Document Management",
        "Electronic Signatures Integration",
        "Cloud-based Infrastructure",
        "Mobile-first Design",
        "API Integration Capabilities",
        "Workflow Automation Engine",
        "Real-time Collaboration Tools"
      ]
    },
    {
      category: "Advanced Procurement Tools",
      items: [
        "Catalog Management System",
        "Contract Lifecycle Management",
        "Strategic Sourcing Tools",
        "Reverse Auction Platform",
        "Price Comparison Engine",
        "Market Research Integration",
        "Supplier Discovery Tools",
        "Procurement Planning Module"
      ]
    },
    {
      category: "Integration & Connectivity",
      items: [
        "ERP System Integration",
        "Accounting Software Connectivity",
        "Bank Payment Gateway Integration",
        "Government Portal Connections",
        "Third-party Marketplace Links",
        "Legacy System Migration",
        "Data Import/Export Tools",
        "Real-time Synchronization"
      ]
    }
  ];

  const industrySpecific = [
    {
      title: "Government & Public Sector",
      icon: <Building className="w-6 h-6 text-blue-400" />,
      description: "PPRA compliant e-procurement solutions for government institutions",
      features: [
        "PPRA Compliance Framework",
        "Open Tender Management",
        "Public Procurement Transparency",
        "Citizen Participation Portal",
        "Government Approval Workflows",
        "Budget Control Systems"
      ]
    },
    {
      title: "Private Sector & Corporations",
      icon: <Briefcase className="w-6 h-6 text-green-400" />,
      description: "Enterprise procurement solutions for private companies",
      features: [
        "Multi-company Management",
        "Decentralized Procurement",
        "Cost Center Management",
        "Vendor Negotiation Tools",
        "Strategic Sourcing",
        "Global Supplier Networks"
      ]
    },
    {
      title: "Healthcare & Medical",
      icon: <UserCheck className="w-6 h-6 text-red-400" />,
      description: "Specialized procurement for healthcare institutions",
      features: [
        "Medical Equipment Procurement",
        "Pharmaceutical Supply Chain",
        "Regulatory Compliance Tracking",
        "Critical Supplies Management",
        "Vendor Certification Verification",
        "Emergency Procurement Protocols"
      ]
    },
    {
      title: "Manufacturing & Production",
      icon: <Package className="w-6 h-6 text-purple-400" />,
      description: "Manufacturing-focused procurement solutions",
      features: [
        "Raw Material Procurement",
        "Just-in-Time Purchasing",
        "Quality Control Integration",
        "Production Planning Sync",
        "Supplier Quality Management",
        "Inventory Optimization"
      ]
    }
  ];

  const benefits = [
    {
      icon: <DollarSign className="w-8 h-8 text-green-400" />,
      title: "Cost Reduction",
      description: "Reduce procurement costs by up to 30% through better supplier management and automated processes",
      metrics: ["30% average cost savings", "25% reduction in processing time", "40% fewer manual errors"]
    },
    {
      icon: <Clock className="w-8 h-8 text-blue-400" />,
      title: "Time Efficiency",
      description: "Accelerate procurement cycles with automated workflows and digital processes",
      metrics: ["50% faster tender processes", "70% quicker approvals", "80% less paperwork"]
    },
    {
      icon: <Shield className="w-8 h-8 text-purple-400" />,
      title: "Enhanced Compliance",
      description: "Ensure 100% compliance with automated policy enforcement and audit trails",
      metrics: ["100% audit trail coverage", "99% compliance rate", "Zero policy violations"]
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-orange-400" />,
      title: "Better Decision Making",
      description: "Make informed decisions with real-time analytics and comprehensive reporting",
      metrics: ["Real-time insights", "360° supplier visibility", "Predictive analytics"]
    }
  ];

  const implementationProcess = [
    {
      phase: "Assessment & Planning",
      description: "Comprehensive analysis of current procurement processes and requirements",
      activities: [
        "Current state assessment",
        "Requirements gathering",
        "Gap analysis",
        "Implementation roadmap",
        "Risk assessment",
        "Resource planning"
      ]
    },
    {
      phase: "System Configuration",
      description: "Custom configuration of the e-procurement platform to match your needs",
      activities: [
        "System setup and configuration",
        "Workflow design and automation",
        "Integration with existing systems",
        "Security implementation",
        "User roles and permissions",
        "Testing and validation"
      ]
    },
    {
      phase: "Data Migration & Training",
      description: "Seamless migration of existing data and comprehensive user training",
      activities: [
        "Data migration and validation",
        "User training programs",
        "Administrator training",
        "Documentation creation",
        "Pilot testing",
        "Performance optimization"
      ]
    },
    {
      phase: "Go-Live & Support",
      description: "Smooth deployment with ongoing support and continuous improvement",
      activities: [
        "System deployment",
        "Go-live support",
        "User support and helpdesk",
        "Performance monitoring",
        "Continuous improvement",
        "Regular system updates"
      ]
    }
  ];

  const gallery = [
    "/images/eproc/1.jpeg",
    "/images/eproc/2.jpeg",
    "/images/eproc/3.jpeg",
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <Globe className="w-4 h-4" /> },
    { id: 'modules', label: 'Core Modules', icon: <Layers className="w-4 h-4" /> },
    { id: 'features', label: 'Features', icon: <CheckSquare className="w-4 h-4" /> },
    { id: 'industries', label: 'Industries', icon: <Building className="w-4 h-4" /> },
    { id: 'benefits', label: 'Benefits', icon: <Target className="w-4 h-4" /> },
    { id: 'implementation', label: 'Implementation', icon: <Settings className="w-4 h-4" /> },
    { id: 'portfolio', label: 'Portfolio', icon: <Award className="w-4 h-4" /> }
  ];

  return (
    <div className="pt-20 bg-gradient-to-br from-slate-200 via-blue-900 to-indigo-200 min-h-screen">
      <Helmet>
        <title>{title} | NorthStart Systems</title>
        <meta name="description" content={description} />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 flex gap-8">
        {/* Sidebar */}
        <div className="hidden lg:block">
          <Sidebar />
        </div>
        
        <main className="flex-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section */}
          <div className="p-8 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border-b border-white/20">
            <Link to="/services" className="inline-flex items-center text-cyan-300 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Services
            </Link>
            
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-5xl font-bold mb-4 text-white leading-tight">
                  {title}
                </h1>
                <p className="text-xl text-slate-200 mb-6 leading-relaxed max-w-4xl">
                  {description}
                </p>
                
                <div className="flex flex-wrap gap-4 mb-8">
                  <div className="flex items-center bg-green-500/20 px-4 py-2 rounded-full">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                    <span className="text-green-200">PPRA Compliant</span>
                  </div>
                  <div className="flex items-center bg-blue-500/20 px-4 py-2 rounded-full">
                    <Shield className="w-5 h-5 text-blue-400 mr-2" />
                    <span className="text-blue-200">Enterprise Security</span>
                  </div>
                  <div className="flex items-center bg-purple-500/20 px-4 py-2 rounded-full">
                    <Zap className="w-5 h-5 text-purple-400 mr-2" />
                    <span className="text-purple-200">Real-time Analytics</span>
                  </div>
                  <div className="flex items-center bg-orange-500/20 px-4 py-2 rounded-full">
                    <Globe className="w-5 h-5 text-orange-400 mr-2" />
                    <span className="text-orange-200">Cloud-based</span>
                  </div>
                </div>
              </div>
              
              <div className="hidden lg:block">
                <div className="bg-white/10 p-6 rounded-2xl border border-white/20">
                  <h3 className="text-lg font-semibold text-white mb-4">Procurement Stats</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-300">Cost Savings</span>
                      <span className="text-emerald-300 font-semibold">30%+</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">Time Reduction</span>
                      <span className="text-emerald-300 font-semibold">50%+</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">Compliance Rate</span>
                      <span className="text-emerald-300 font-semibold">99%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">User Satisfaction</span>
                      <span className="text-emerald-300 font-semibold">95%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="px-8 py-4 bg-white/5 border-b border-white/10">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-6 py-3 rounded-xl transition-all ${
                    activeTab === tab.id
                      ? 'bg-emerald-500/30 text-white border border-emerald-400/50'
                      : 'bg-white/10 text-slate-300 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {tab.icon}
                  <span className="ml-2 font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="p-8 text-white">
            {activeTab === 'overview' && (
              <div className="space-y-12">
                {/* Key Value Propositions */}
                <section>
                  <h2 className="text-3xl font-bold mb-8 text-center">Why Choose Our E-Procurement Solution?</h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="text-center p-6 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-2xl border border-emerald-500/20">
                      <DollarSign className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Cost Effective</h3>
                      <p className="text-slate-300 text-sm">Reduce procurement costs by up to 30% with automated processes</p>
                    </div>
                    <div className="text-center p-6 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl border border-blue-500/20">
                      <Clock className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Time Efficient</h3>
                      <p className="text-slate-300 text-sm">Speed up procurement cycles by 50% with digital workflows</p>
                    </div>
                    <div className="text-center p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl border border-purple-500/20">
                      <Shield className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Fully Compliant</h3>
                      <p className="text-slate-300 text-sm">100% PPRA compliant with built-in audit trails</p>
                    </div>
                    <div className="text-center p-6 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-2xl border border-orange-500/20">
                      <BarChart3 className="w-12 h-12 text-orange-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Data-Driven</h3>
                      <p className="text-slate-300 text-sm">Make informed decisions with real-time analytics</p>
                    </div>
                  </div>
                </section>

                {/* Problem & Solution */}
                <section className="grid md:grid-cols-2 gap-8">
                  <div className="bg-red-500/10 p-8 rounded-2xl border border-red-500/20">
                    <h3 className="text-2xl font-semibold mb-6 text-red-300">Common Procurement Challenges</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-200">Manual, paper-based processes leading to delays</span>
                      </li>
                      <li className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-200">Lack of transparency in supplier selection</span>
                      </li>
                      <li className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-200">Poor spend visibility and budget control</span>
                      </li>
                      <li className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-200">Compliance and audit trail issues</span>
                      </li>
                      <li className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-200">Inefficient supplier management</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-emerald-500/10 p-8 rounded-2xl border border-emerald-500/20">
                    <h3 className="text-2xl font-semibold mb-6 text-emerald-300">Our Solutions</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-200">Fully digital, automated procurement workflows</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-200">Transparent supplier evaluation and selection</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-200">Real-time spend analytics and budget tracking</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-200">Built-in compliance and comprehensive audit trails</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-200">Advanced supplier lifecycle management</span>
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Technology Stack */}
                <section>
                  <h2 className="text-3xl font-bold mb-8 text-center">Built with Modern Technology</h2>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 p-6 rounded-2xl border border-blue-500/20">
                      <Database className="w-8 h-8 text-blue-400 mb-4" />
                      <h3 className="text-xl font-semibold mb-3">Secure Database</h3>
                      <p className="text-slate-300 mb-4">Enterprise-grade database security with encryption and backup</p>
                      <ul className="text-sm text-slate-400 space-y-1">
                        <li>• PostgreSQL/MongoDB</li>
                        <li>• Data encryption at rest</li>
                        <li>• Automated backups</li>
                        <li>• High availability</li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 p-6 rounded-2xl border border-emerald-500/20">
                      <Globe className="w-8 h-8 text-emerald-400 mb-4" />
                      <h3 className="text-xl font-semibold mb-3">Cloud Platform</h3>
                      <p className="text-slate-300 mb-4">Scalable cloud infrastructure with 99.9% uptime guarantee</p>
                      <ul className="text-sm text-slate-400 space-y-1">
                        <li>• AWS/Azure hosting</li>
                        <li>• Auto-scaling</li>
                        <li>• Load balancing</li>
                        <li>• CDN integration</li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 p-6 rounded-2xl border border-orange-500/20">
                      <Shield className="w-8 h-8 text-orange-400 mb-4" />
                      <h3 className="text-xl font-semibold mb-3">Security First</h3>
                      <p className="text-slate-300 mb-4">Multi-layered security with compliance certifications</p>
                      <ul className="text-sm text-slate-400 space-y-1">
                        <li>• SSL/TLS encryption</li>
                        <li>• Multi-factor authentication</li>
                        <li>• Role-based access</li>
                        <li>• Security auditing</li>
                      </ul>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'modules' && (
              <div className="space-y-8">
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold mb-4">Comprehensive E-Procurement Modules</h2>
                  <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                    Our modular e-procurement platform covers every aspect of your procurement process, 
                    from supplier management to final payment processing.
                  </p>
                </div>

                <div className="grid gap-8">
                  {Object.values(coreModules).map((module, idx) => (
                    <div key={idx} className="bg-white/5 rounded-2xl border border-white/10 p-8 hover:bg-white/10 transition-all">
                      <div className="flex items-start gap-6">
                        <div className="flex-shrink-0 p-4 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-2xl border border-emerald-500/30">
                          {module.icon}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold mb-3">{module.title}</h3>
                          <p className="text-slate-300 mb-6 text-lg">{module.description}</p>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {module.features.map((feature, featureIdx) => (
                              <div key={featureIdx} className="flex items-center p-3 bg-white/5 rounded-lg">
                                <CheckCircle className="w-4 h-4 text-emerald-400 mr-3 flex-shrink-0" />
                                <span className="text-slate-200 text-sm">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'features' && (
              <div className="space-y-8">
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold mb-4">Advanced Features & Capabilities</h2>
                  <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                    Discover the extensive range of features that make our e-procurement solution 
                    the most comprehensive in the market.
                  </p>
                </div>

                <div className="grid gap-8">
                  {additionalFeatures.map((category, idx) => (
                    <div key={idx} className="bg-white/5 rounded-2xl border border-white/10 p-8">
                      <h3 className="text-2xl font-semibold mb-6 flex items-center">
                        <Settings className="w-6 h-6 text-emerald-400 mr-3" />
                        {category.category}
                      </h3>
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {category.items.map((item, itemIdx) => (
                          <div key={itemIdx} className="flex items-center p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-all">
                            <CheckCircle className="w-4 h-4 text-emerald-400 mr-3 flex-shrink-0" />
                            <span className="text-slate-200 text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Procurement Workflow */}
                <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-2xl border border-emerald-500/20 p-8">
                  <h3 className="text-2xl font-semibold mb-8 text-center">Complete Procurement Workflow</h3>
                  <div className="grid md:grid-cols-5 gap-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Search className="w-8 h-8 text-emerald-400" />
                      </div>
                      <h4 className="font-semibold mb-2">Requisition</h4>
                      <p className="text-sm text-slate-300">Create and submit purchase requests</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FileText className="w-8 h-8 text-emerald-400" />
                      </div>
                      <h4 className="font-semibold mb-2">Tendering</h4>
                      <p className="text-sm text-slate-300">Publish tenders and collect bids</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Users className="w-8 h-8 text-emerald-400" />
                      </div>
                      <h4 className="font-semibold mb-2">Evaluation</h4>
                      <p className="text-sm text-slate-300">Evaluate and select suppliers</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <ShoppingCart className="w-8 h-8 text-emerald-400" />
                      </div>
                      <h4 className="font-semibold mb-2">Ordering</h4>
                      <p className="text-sm text-slate-300">Generate and send purchase orders</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CreditCard className="w-8 h-8 text-emerald-400" />
                      </div>
                      <h4 className="font-semibold mb-2">Payment</h4>
                      <p className="text-sm text-slate-300">Process invoices and payments</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'industries' && (
              <div className="space-y-8">
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold mb-4">Industry-Specific Solutions</h2>
                  <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                    Tailored e-procurement solutions designed to meet the unique requirements 
                    of different industries and sectors.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {industrySpecific.map((industry, idx) => (
                    <div key={idx} className="bg-white/5 rounded-2xl border border-white/10 p-8 hover:bg-white/10 transition-all">
                      <div className="flex items-start gap-4 mb-6">
                        {industry.icon}
                        <div>
                          <h3 className="text-2xl font-semibold mb-2">{industry.title}</h3>
                          <p className="text-slate-300">{industry.description}</p>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        {industry.features.map((feature, featureIdx) => (
                          <div key={featureIdx} className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-emerald-400 mr-3 flex-shrink-0" />
                            <span className="text-slate-200 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Compliance Standards */}
                <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl border border-blue-500/20 p-8">
                  <h3 className="text-2xl font-semibold mb-6 text-center">Compliance & Standards</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center p-4">
                      <Shield className="w-12 h-12 text-blue-400 mx-auto mb-3" />
                      <h4 className="font-semibold mb-2">PPRA Compliance</h4>
                      <p className="text-sm text-slate-300">Full compliance with Public Procurement Regulatory Authority guidelines</p>
                    </div>
                    <div className="text-center p-4">
                      <FileCheck className="w-12 h-12 text-purple-400 mx-auto mb-3" />
                      <h4 className="font-semibold mb-2">ISO Standards</h4>
                      <p className="text-sm text-slate-300">Adherence to ISO 9001 quality management standards</p>
                    </div>
                    <div className="text-center p-4">
                      <UserCheck className="w-12 h-12 text-green-400 mx-auto mb-3" />
                      <h4 className="font-semibold mb-2">Data Protection</h4>
                      <p className="text-sm text-slate-300">GDPR compliant data handling and privacy protection</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'benefits' && (
              <div className="space-y-8">
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold mb-4">Measurable Business Benefits</h2>
                  <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                    Transform your procurement operations and achieve significant cost savings, 
                    efficiency gains, and compliance improvements.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {benefits.map((benefit, idx) => (
                    <div key={idx} className="bg-white/5 rounded-2xl border border-white/10 p-8 hover:bg-white/10 transition-all">
                      <div className="flex items-start gap-6">
                        <div className="flex-shrink-0 p-3 bg-white/10 rounded-2xl">
                          {benefit.icon}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold mb-3">{benefit.title}</h3>
                          <p className="text-slate-300 mb-6">{benefit.description}</p>
                          <div className="space-y-2">
                            {benefit.metrics.map((metric, metricIdx) => (
                              <div key={metricIdx} className="flex items-center">
                                <TrendingUp className="w-4 h-4 text-emerald-400 mr-3" />
                                <span className="text-emerald-300 font-semibold">{metric}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* ROI Calculator */}
                <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-2xl border border-green-500/20 p-8">
                  <div className="text-center mb-8">
                    <Calculator className="w-16 h-16 text-green-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-semibold mb-4">Return on Investment</h3>
                    <p className="text-slate-300">See how much you can save with our e-procurement solution</p>
                  </div>
                  
                  <div className="grid md:grid-cols-4 gap-6">
                    <div className="text-center p-6 bg-white/10 rounded-xl">
                      <div className="text-3xl font-bold text-green-400 mb-2">6 months</div>
                      <div className="text-slate-300">Typical ROI Period</div>
                    </div>
                    <div className="text-center p-6 bg-white/10 rounded-xl">
                      <div className="text-3xl font-bold text-green-400 mb-2">30%</div>
                      <div className="text-slate-300">Average Cost Reduction</div>
                    </div>
                    <div className="text-center p-6 bg-white/10 rounded-xl">
                      <div className="text-3xl font-bold text-green-400 mb-2">50%</div>
                      <div className="text-slate-300">Time Savings</div>
                    </div>
                    <div className="text-center p-6 bg-white/10 rounded-xl">
                      <div className="text-3xl font-bold text-green-400 mb-2">300%</div>
                      <div className="text-slate-300">3-Year ROI</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'implementation' && (
              <div className="space-y-8">
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold mb-4">Implementation & Support Process</h2>
                  <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                    Our structured implementation approach ensures smooth deployment with minimal 
                    disruption to your existing operations.
                  </p>
                </div>

                <div className="grid gap-8">
                  {implementationProcess.map((phase, idx) => (
                    <div key={idx} className="bg-white/5 rounded-2xl border border-white/10 p-8">
                      <div className="flex items-start gap-6">
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-full flex items-center justify-center border border-emerald-500/30">
                          <span className="text-emerald-400 font-bold text-lg">{idx + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold mb-3">{phase.phase}</h3>
                          <p className="text-slate-300 mb-6">{phase.description}</p>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {phase.activities.map((activity, activityIdx) => (
                              <div key={activityIdx} className="flex items-center p-3 bg-white/5 rounded-lg">
                                <CheckCircle className="w-4 h-4 text-emerald-400 mr-3 flex-shrink-0" />
                                <span className="text-slate-200 text-sm">{activity}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Timeline */}
                <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl border border-blue-500/20 p-8">
                  <h3 className="text-2xl font-semibold mb-8 text-center">Implementation Timeline</h3>
                  <div className="grid md:grid-cols-4 gap-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-blue-300 font-bold">Week 1-2</span>
                      </div>
                      <h4 className="font-semibold mb-2">Assessment</h4>
                      <p className="text-sm text-slate-300">Requirements analysis and planning</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-purple-300 font-bold">Week 3-6</span>
                      </div>
                      <h4 className="font-semibold mb-2">Setup</h4>
                      <p className="text-sm text-slate-300">System configuration and customization</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-emerald-300 font-bold">Week 7-8</span>
                      </div>
                      <h4 className="font-semibold mb-2">Training</h4>
                      <p className="text-sm text-slate-300">User training and testing</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-green-300 font-bold">Week 9</span>
                      </div>
                      <h4 className="font-semibold mb-2">Go Live</h4>
                      <p className="text-sm text-slate-300">System deployment and support</p>
                    </div>
                  </div>
                </div>

                {/* Support Options */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                    <Headphones className="w-8 h-8 text-cyan-400 mb-4" />
                    <h4 className="text-lg font-semibold mb-3">24/7 Support</h4>
                    <p className="text-slate-300 text-sm mb-4">Round-the-clock technical support and maintenance</p>
                    <ul className="text-sm text-slate-400 space-y-1">
                      <li>• Phone & email support</li>
                      <li>• Remote assistance</li>
                      <li>• System monitoring</li>
                      <li>• Regular updates</li>
                    </ul>
                  </div>

                  <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                    <Users className="w-8 h-8 text-purple-400 mb-4" />
                    <h4 className="text-lg font-semibold mb-3">Training Programs</h4>
                    <p className="text-slate-300 text-sm mb-4">Comprehensive training for all user levels</p>
                    <ul className="text-sm text-slate-400 space-y-1">
                      <li>• Administrator training</li>
                      <li>• End-user workshops</li>
                      <li>• Online tutorials</li>
                      <li>• Documentation</li>
                    </ul>
                  </div>

                  <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                    <Settings className="w-8 h-8 text-orange-400 mb-4" />
                    <h4 className="text-lg font-semibold mb-3">Maintenance</h4>
                    <p className="text-slate-300 text-sm mb-4">Ongoing system maintenance and optimization</p>
                    <ul className="text-sm text-slate-400 space-y-1">
                      <li>• Regular backups</li>
                      <li>• Security updates</li>
                      <li>• Performance tuning</li>
                      <li>• Feature enhancements</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'portfolio' && (
              <div className="space-y-8">
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold mb-4">Our E-Procurement Portfolio</h2>
                  <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                    Explore successful e-procurement implementations across various sectors 
                    and see how we've transformed procurement operations.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                  {gallery.map((img, idx) => (
                    <div key={idx} className="group relative overflow-hidden rounded-2xl">
                      <img
                        src={img}
                        alt={`E-procurement solution ${idx + 1}`}
                        className="w-full h-64 object-cover transition-transform group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-white font-semibold mb-1">E-Procurement System {idx + 1}</h3>
                          <p className="text-slate-200 text-sm">Complete procurement automation solution</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Success Stories */}
                <div className="grid md:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white/5 rounded-2xl border border-white/10 p-8">
                    <div className="flex items-center mb-6">
                      <Building className="w-8 h-8 text-blue-400 mr-3" />
                      <h3 className="text-xl font-semibold">Government Ministry</h3>
                    </div>
                    <p className="text-slate-300 mb-4">
                      Implemented comprehensive e-procurement system reducing processing time by 60% 
                      and achieving 100% PPRA compliance.
                    </p>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-emerald-400">60%</div>
                        <div className="text-sm text-slate-400">Time Saved</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-emerald-400">40%</div>
                        <div className="text-sm text-slate-400">Cost Reduction</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-emerald-400">100%</div>
                        <div className="text-sm text-slate-400">Compliance</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-2xl border border-white/10 p-8">
                    <div className="flex items-center mb-6">
                      <Briefcase className="w-8 h-8 text-purple-400 mr-3" />
                      <h3 className="text-xl font-semibold">Manufacturing Company</h3>
                    </div>
                    <p className="text-slate-300 mb-4">
                      Streamlined supplier management and automated purchase orders, 
                      resulting in significant operational efficiency improvements.
                    </p>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-emerald-400">45%</div>
                        <div className="text-sm text-slate-400">Faster Processing</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-emerald-400">35%</div>
                        <div className="text-sm text-slate-400">Cost Savings</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-emerald-400">90%</div>
                        <div className="text-sm text-slate-400">User Satisfaction</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-2xl border border-emerald-500/20 p-8 text-center">
                  <h3 className="text-3xl font-semibold mb-4">Ready to Transform Your Procurement?</h3>
                  <p className="text-slate-300 mb-8 max-w-2xl mx-auto text-lg">
                    Join hundreds of organizations that have revolutionized their procurement operations 
                    with our comprehensive e-procurement solution. Get started today with a free consultation.
                  </p>
                  <div className="flex flex-wrap justify-center gap-6">
                    <button className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all transform hover:scale-105 flex items-center">
                      <Phone className="w-5 h-5 mr-2" />
                      Schedule Demo
                    </button>
                    <button className="bg-white/10 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all border border-white/20 flex items-center">
                      <Mail className="w-5 h-5 mr-2" />
                      Request Quote
                    </button>
                    <button className="bg-transparent text-emerald-300 px-8 py-4 rounded-xl font-semibold hover:bg-emerald-500/10 transition-all border border-emerald-500/30 flex items-center">
                      <FileText className="w-5 h-5 mr-2" />
                      Download Brochure
                    </button>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-white/5 rounded-2xl border border-white/10 p-8">
                  <h3 className="text-2xl font-semibold mb-6 text-center">Get In Touch</h3>
                  <div className="grid md:grid-cols-3 gap-6 text-center">
                    <div className="flex flex-col items-center">
                      <Phone className="w-8 h-8 text-cyan-400 mb-3" />
                      <h4 className="font-semibold mb-2">Phone</h4>
                      <p className="text-slate-300">+254 700 041385</p>
                      <p className="text-slate-400 text-sm">Available 24/7</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <Mail className="w-8 h-8 text-emerald-400 mb-3" />
                      <h4 className="font-semibold mb-2">Email</h4>
                      <p className="text-slate-300">info@northstartsystems.com</p>
                      <p className="text-slate-400 text-sm">Response within 2 hours</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <MapPin className="w-8 h-8 text-purple-400 mb-3" />
                      <h4 className="font-semibold mb-2">Location</h4>
                      <p className="text-slate-300">Nairobi, Kenya</p>
                      <p className="text-slate-400 text-sm">On-site consultations available</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default EProcurement;